import type { VertexProvenance } from "../../wasm/editOps.ts";
import { provenanceFromCompactRemap } from "../provenance.ts";
import {
  compactUnreferencedVertices,
  identityRemap,
  rebuildTopologyEdgesPreservingWire,
  triangulateTopologyForRendering,
} from "../topology.ts";
import type { PolygonTopology, RenderTriangleIndices } from "../types.ts";
import { EDGE_WIRE } from "../types.ts";

/** Count wire-flagged edges in a topology. */
function countWireEdges(topo: PolygonTopology): number {
  let n = 0;
  for (let i = 0; i < topo.edgeFlags.length; i++) {
    if (topo.edgeFlags[i] & EDGE_WIRE) n++;
  }
  return n;
}

/**
 * Clean up degenerate geometry: remove duplicate faces, faces with
 * duplicate vertices, degenerate wire edges, and orphaned vertices.
 *
 * Returns null if no issues were found and the mesh is already clean.
 */
export function cleanUpMesh(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
): {
  vertices: Float32Array;
  indices: RenderTriangleIndices;
  topology: PolygonTopology;
  provenance: VertexProvenance;
  removedFaces: number;
  removedWireEdges: number;
  removedVertices: number;
} | null {
  const { offsets, corners } = topology;
  const faceCount = offsets.length - 1;

  // --- Pass 1: Remove degenerate and duplicate faces ---
  const faceKeys = new Set<string>();
  const newCorners: number[] = [];
  const newOffsets: number[] = [0];
  let removedFaces = 0;

  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;

    // Skip faces with fewer than 3 corners
    if (size < 3) {
      removedFaces++;
      continue;
    }

    // Skip faces with duplicate vertex indices
    const faceVerts: number[] = [];
    const seen = new Set<number>();
    let hasDup = false;
    for (let j = 0; j < size; j++) {
      const vi = corners[start + j];
      if (seen.has(vi)) {
        hasDup = true;
        break;
      }
      seen.add(vi);
      faceVerts.push(vi);
    }
    if (hasDup) {
      removedFaces++;
      continue;
    }

    // Skip duplicate faces (same vertex set regardless of winding/start)
    const sorted = [...faceVerts].sort((a, b) => a - b);
    const key = sorted.join(",");
    if (faceKeys.has(key)) {
      removedFaces++;
      continue;
    }
    faceKeys.add(key);

    // Face survives
    for (const vi of faceVerts) newCorners.push(vi);
    newOffsets.push(newCorners.length);
  }

  // --- Pass 2: Rebuild edges, preserving valid wire edges ---
  // `rebuildTopologyEdgesPreservingWire` already drops degenerate wire
  // edges (endpoints collapse to the same vertex) and deduplicates wire
  // edges against face-bound edges. It's the single source of truth for
  // wire preservation; we compare before/after wire counts for reporting.
  const cleanTopo = rebuildTopologyEdgesPreservingWire(
    new Uint32Array(newOffsets),
    new Uint32Array(newCorners),
    topology,
    identityRemap,
  );
  const removedWireEdges = countWireEdges(topology) - countWireEdges(cleanTopo);

  // --- Pass 3: Remove orphaned vertices ---
  const {
    vertices: finalVerts,
    topology: finalTopo,
    remap: orphanRemap,
  } = compactUnreferencedVertices(vertices, cleanTopo);
  const removedVertices = vertices.length / 3 - finalVerts.length / 3;

  // Nothing to do
  if (removedFaces === 0 && removedWireEdges === 0 && removedVertices === 0) {
    return null;
  }

  const indices = triangulateTopologyForRendering(finalTopo);
  // Provenance: face/wire culling doesn't move vertices, only the orphan
  // compaction in pass 3 renumbers them. The remap returned by
  // `compactUnreferencedVertices` is already the canonical OLD → NEW table
  // we need; build the provenance directly off it.
  const provenance = provenanceFromCompactRemap(orphanRemap);
  return {
    vertices: finalVerts,
    indices,
    topology: finalTopo,
    provenance,
    removedFaces,
    removedWireEdges,
    removedVertices,
  };
}
